import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react'
import { useNotificationStore, type Toast } from '@/stores/useNotificationStore'
import { Button } from './Button'

const icons = {
  success: <CheckCircle2 className="w-5 h-5 text-success" />,
  error: <XCircle className="w-5 h-5 text-error" />,
  info: <Info className="w-5 h-5 text-info" />,
  warning: <AlertTriangle className="w-5 h-5 text-warning" />,
}

const alertClass = {
  success: 'alert-success',
  error: 'alert-error',
  info: 'alert-info',
  warning: 'alert-warning',
}

function ToastItem({ toast }: { toast: Toast }) {
  const { t: tc } = useTranslation('common')
  const removeToast = useNotificationStore((s) => s.removeToast)

  useEffect(() => {
    const timer = setTimeout(() => removeToast(toast.id), 4000)
    return () => clearTimeout(timer)
  }, [toast.id, removeToast])

  return (
    <div className={`alert ${alertClass[toast.type]} shadow-lg animate-slide-in`}>
      {icons[toast.type]}
      <span className="text-sm">{toast.message}</span>
      {toast.action && (
        <Link
          to={toast.action.to}
          className="text-sm font-semibold underline whitespace-nowrap"
          onClick={() => removeToast(toast.id)}
        >
          {toast.action.label}
        </Link>
      )}
      <Button variant="ghost" size="xs" onClick={() => removeToast(toast.id)} aria-label={tc('actions.close')}>
        <X className="w-4 h-4" />
      </Button>
    </div>
  )
}

export function ToastContainer() {
  const toasts = useNotificationStore((s) => s.toasts)

  if (toasts.length === 0) return null

  return (
    <div
      data-testid="toast-container"
      className={
        // Mobile: centered horizontally, BELOW the safe-area inset (never under
        // the status bar / notch). Desktop (sm+): top-right, fixed width.
        // NOTE: the native Capacitor shell reports env(safe-area-inset-top)=0,
        // so the real status-bar clearance is forced via a `.native-shell`
        // rule in index.css (same mechanism as the onboarding top bar).
        'fixed z-[100] flex flex-col gap-2 ' +
        'top-[calc(env(safe-area-inset-top)+0.75rem)] left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-sm ' +
        'sm:left-auto sm:translate-x-0 sm:right-4 sm:top-4 sm:w-80'
      }
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
